import { Component } from '@theme/component';

/**
 * A mobile slider for the editorial journal gallery.
 *
 * On mobile the two slides collapse into a horizontally scrollable, snap-aligned
 * track. This component keeps the pagination indicator in sync with the slide the
 * user has scrolled to. On desktop the dots are hidden via CSS, so the work here
 * is a no-op there.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track - The scrollable slide track
 * @property {HTMLElement[]} dots - The pagination indicators
 *
 * @extends {Component<Refs>}
 */
export class EditorialJournalGalleryComponent extends Component {
  requiredRefs = ['track', 'dots'];

  #frame = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.track?.removeEventListener('scroll', this.#onScroll);
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    this.#frame = null;

    const { track, dots } = this.refs;
    if (!track || !dots?.length) return;

    const index = Math.round(track.scrollLeft / track.clientWidth);

    dots.forEach((dot, i) => {
      dot.classList.toggle('editorial-journal-gallery__dot--active', i === index);
    });
  }
}

if (!customElements.get('editorial-journal-gallery-component')) {
  customElements.define('editorial-journal-gallery-component', EditorialJournalGalleryComponent);
}
